import moment from 'moment';
import store from 'store/index';
import Api from 'services/Api';
import {determineLife, calcAgeDeath, extractIncome, incomeValid, isChild} from 'services/personFunctions';
import {validDate} from 'components/form/validator/validations';
import {areAllTrue, dateFromServer} from 'services/helper';
import _ from 'lodash';


/** ********************************************************
 *                       Mutations                        *
 **********************************************************/

/**
 * Mutation types
 * @type {{UPDATE_DECEASED: string, UPDATE_DECEASED_LIFE_EXPECTANCY: string, UPDATE_DECEASED_INCOME: string, ADD_DEPENDANT: string, UPDATE_DEPENDANT: string, DELETE_DEPENDANT: string, UPDATE_DEPENDANT_LIFE_EXPECTANCY: string, UPDATE_DEPENDANT_INCOME: string}}
 */
export const mutationTypes = {
    UPDATE_DECEASED: 'UPDATE_DECEASED', // Update a value on the deceased
    RESET_DECEASED: 'RESET_DECEASED', // Reset the deceased
    SERVER_UPDATE_DECEASED: 'SERVER_UPDATE_DECEASED', // Update all values from the server
    SERVER_UPDATE_DECEASED_INCOME: 'SERVER_UPDATE_DECEASED_INCOME', // Update all values from the server
    UPDATE_DECEASED_LIFE_EXPECTANCY: 'UPDATE_DECEASED_LIFE_EXPECTANCY', // Update a value on deceased .lifExpectancy property
    UPDATE_DECEASED_INCOME: 'UPDATE_DECEASED_INCOME', // Update a value on the deceased
    ADD_DEPENDANT: 'ADD_DEPENDANT', // Add  a dependant to state
    UPDATE_DEPENDANT: 'UPDATE_DEPENDANT', // Update a value on a dependant
    RESET_DEPENDANTS: 'RESET_DEPENDANTS', // Resets all dependants
    SERVER_UPDATE_DEPENDANT: 'SERVER_UPDATE_DEPENDANT', // Update all on all a dependant from the server
    SERVER_UPDATE_DEPENDANT_INCOME: 'SERVER_UPDATE_DEPENDANT_INCOME', // Update all on all a dependant from the server
    DELETE_DEPENDANT: 'DELETE_DEPENDANT', // Delete dependant
    UPDATE_DEPENDANT_LIFE_EXPECTANCY: 'UPDATE_DEPENDANT_LIFE_EXPECTANCY', // As above for a given dependant
    UPDATE_DEPENDANT_INCOME: 'UPDATE_DEPENDANT_INCOME', // As a about for a given dependant,
    ADD_DECEASED_INCOME_PERIOD: 'ADD_DECEASED_INCOME_PERIOD',
    UPDATE_DECEASED_INCOME_PERIOD: 'UPDATE_DECEASED_INCOME_PERIOD',
    DELETE_DECEASED_INCOME_PERIOD: 'DELETE_DECEASED_INCOME_PERIOD',
    ADD_DEPENDANT_INCOME_PERIOD: 'ADD_DEPENDANT_INCOME_PERIOD',
    UPDATE_DEPENDANT_INCOME_PERIOD: 'UPDATE_DEPENDANT_INCOME_PERIOD',
    DELETE_DEPENDANT_INCOME_PERIOD: 'DELETE_DEPENDANT_INCOME_PERIOD',
    UPDATE_DEPENDENCY_ON_INCOME_VALID: 'UPDATE_DEPENDENCY_ON_INCOME_VALID'
};

/** ********************************************************
 *                         State                          *
 **********************************************************/

/**
 * Default retirement ages
 *
 * @type {{male: number, female: number}}
 */
const DEFAULT_RETIREMENT = {
    male: 65,
    female: 62.5,
};

function personTemplate() {
    return {
        name: '',
        dateOfBirth: null,
        dateOfDeath: null,
        onsetOfSymptoms: null,
        ageAtDeath: null,
        currentAge: null,
        gender: null,
        predictedDeath: null,
        levelOfEducation: null,
        income: incomeTemplate(),
        lifeExpectancy: lifeExpectancyTemplate(),
    };
}

/**
 * Template for storing a persons income
 * @return {{retired: boolean, pensionIncomeType: string, netPensionIncome: null, unchangedPension: boolean, grossPensionIncome: null, retirementAge: number, retirementDate: string, grossIncome: null, netIncome: number, employmentType: string, periodResults: Array}}
 */
function incomeTemplate() {
    return {
        retired: true,
        retirementAge: 65,
        retirementDate: '',
        periods: [],
    };
}

const defaultIncomePeriodTemplate = ({start, end}) => {
    return {
        startOfPeriod: start,
        endOfPeriod: end,
        employmentType: 'employed',
        employmentIncome: 0,
        employmentNetGross: 'net',
        pensionIncome: 0,
        pensionNetGross: 'net',
        totalGrossIncome: 0,
        totalNetIncome: 0,
    }
};

const incomePeriodTemplate = ({start, end, employmentType, employmentIncome, employmentNetGross, pensionIncome, pensionNetGross, totalGrossIncome, totalNetIncome}) => {
    return {
        startOfPeriod: start,
        endOfPeriod: end,
        employmentType: employmentType,
        employmentIncome: employmentIncome,
        employmentNetGross: employmentNetGross,
        pensionIncome: pensionIncome,
        pensionNetGross: pensionNetGross,
        totalGrossIncome: totalGrossIncome,
        totalNetIncome: totalNetIncome,
    }
};

/**
 * Template for a persons life expectancy
 * @return {{method: string, yearsAndMonths: {months: number, years: number}, dateOfDeathIsAssumed: null|string, ageAtDeath: null|number, dateOfDeath: null|string}}
 */
function lifeExpectancyTemplate() {
    return {
        method: 'ogden_table',
        yearsAndMonths: {
            months: 0,
            years: 0,
        },
        dateOfDeathIsAssumed: null,
        ageAtDeath: null,
        dateOfDeath: null,
        percentageOfNormalLifeExpectancy: 100,
        percentageReductionInNormalLifeExpectancy: 0,
        child: {
            youthCutOffAge: null,
            youthYearsLeft: null,
            youthCutOffDate: null,
        },
    };
}

// Initial State
const state = {
    theDeceased: personTemplate(),
    dependants: [],
    dependencyOnIncomeValid: false,
};

/** ********************************************************
 *                         GETTERS                        *
 **********************************************************/


// /////////////////////////////////////////////////////////
//                  Helper Functions                     //
// /////////////////////////////////////////////////////////


/**
 * Find the next start date for a income period from start.
 *
 * @param periods
 * @param worseCase
 */
const findNextPeriodStart = (periods, worseCase) => periods.reduce((min, period) => {
    const periodDate = moment(period.endOfPeriod, 'DD/MM/YYYY');
    return periodDate.isAfter(min) ? periodDate : min;
}, moment(worseCase, 'DD/MM/YYYY')).format('DD/MM/YYYY');

const findNextPeriodEnd = (periods, worseCase) => {
    return periods.reduce((max, period) => {
        let periodDate = moment(period.startOfPeriod, 'DD/MM/YYYY');
        return periodDate.isAfter(max) ? periodDate : max;
    }, moment(worseCase, 'DD/MM/YYYY')).format('DD/MM/YYYY');
};

/**
 * Determine the length of a dependency compared to deceased, life expectancy or if the person is under specified age.
 *
 * @param {state} state
 * @param {number} index dependant to compare to
 * @return {{duration:number,date:string, type:string}}
 */
const determineDependency = (state, getters) => (index) => {
    if (!state.dependants[index]) {
        return {};
    }
    const deceasedButForDate = state.theDeceased.lifeExpectancy.dateOfDeath;
    const assumedSettlement = getters['fatal/estate/proceduralDates/date']('dateOfAssumedSettlement');
    const dependantDateOfDeath = state.dependants[index].lifeExpectancy.dateOfDeath;
    const mDeceasedButForDate = moment(deceasedButForDate, 'DD/MM/YYYY');
    const mAssumedSettlement = moment(assumedSettlement, 'DD/MM/YYYY');
    const mDependantDob = moment(state.dependants[index].dateOfBirth, 'DD/MM/YYYY');
    const mDependantDateOfDeath = moment(dependantDateOfDeath, 'DD/MM/YYYY');

    // If dates are valid
    if (!validDate(assumedSettlement) || !validDate(dependantDateOfDeath)) return {};

    // If the claimant is under specified dependency age
    if (Math.abs(mDependantDob.diff(moment(), 'years')) < 21) {
        let dependencyEndAge = 18;
        switch (state.dependants[index].lifeExpectancy.method) {
            case 'dependent_until_custom':
                dependencyEndAge = state.dependants[index].lifeExpectancy.child.youthCutOffAge;
                break;
            case 'dependent_until_16':
                dependencyEndAge = 16;
                break;
            case 'dependent_until_21':
                dependencyEndAge = 21;
                break;
        }

        const dependencyEnd = mDependantDob.add(dependencyEndAge, 'years');

        if (mAssumedSettlement.isAfter(dependencyEnd) || mDeceasedButForDate.isAfter(dependencyEndAge)) {
            return {
                duration: dependencyEnd.diff(mAssumedSettlement, 'years', true),
                date: dependencyEnd.format('DD/MM/YYYY'),
                type: `To dependant's age of ${dependencyEndAge}`,
            };
        }
    }

    if (mAssumedSettlement.isAfter(mDependantDateOfDeath) || mDeceasedButForDate.isAfter(mDependantDateOfDeath)) {
        return {
            duration: mDependantDateOfDeath.diff(mAssumedSettlement, 'years', true),
            date: mDependantDateOfDeath.format('DD/MM/YYYY'),
            type: "To end of dependant's life expectancy",
        };
    }

    return {
        duration: mDeceasedButForDate.diff(mAssumedSettlement, 'years', true),
        date: mDeceasedButForDate.format('DD/MM/YYYY'),
        type: "To end of the deceased's life expectancy",
    };
};

const valueFromState = (state, key) => state[key];

/**
 * is a dependant valid?
 *
 * Doe they have a name, dob, gender and life expectcy
 *
 * @param dependant
 */
const dependantValid =
    (dependant) => {
        const valid =
            (!!dependant.name)
            && (!!dependant.dateOfBirth)
            && (!!dependant.gender);
        return valid;
    };

const dependantsNoChildren = dependants => dependants.filter(dependant => !isChild(dependant));

// /////////////////////////////////////////////////////////
//                     Public Getters                    //
// /////////////////////////////////////////////////////////
const getters = {
    theDeceased: state => valueFromState(state, 'theDeceased'),
    theDeceasedValue: state => key => valueFromState(state.theDeceased, [key]),
    theDeceasedIncomeValid: state => {
        let assumedSettlement = moment(store.getters['fatal/estate/proceduralDates/date']('dateOfAssumedSettlement'), 'DD/MM/YYYY');
        
        return incomeValid(assumedSettlement, state.theDeceased)
    },
    durationDeceasedDependency: state => state.theDeceased.lifeExpectancy.ageAtDeath - state.theDeceased.ageAtDeath,
    dependant: state => index => valueFromState(state.dependants, index),
    dependants: state => valueFromState(state, 'dependants'),
    dependantsNoChild: state => dependantsNoChildren(valueFromState(state, 'dependants')),
    hasMultipleDependants: state => valueFromState(state, 'dependants').length > 1,
    noOfDependants: state => valueFromState(state, 'dependants').length,
    allDependantsValid: state => areAllTrue(valueFromState(state, 'dependants'), dependantValid),
    dependantIncomeValid: state => index => {
        let assumedSettlement = moment(store.getters['fatal/estate/proceduralDates/date']('dateOfAssumedSettlement'), 'DD/MM/YYYY');
        
        return incomeValid(assumedSettlement, state.theDependants[index])
    },
    allDependantsIncomeValid: state => areAllTrue(state.dependants, incomeValid),
    theDeceasedNetIncome: state => state.theDeceased.income.netIncome + state.theDeceased.income.netPensionIncome,
    theDeceasedGrossIncome: state => state.theDeceased.income.grossIncome + state.theDeceased.income.grossPensionIncome,
    dependantNetIncome: state => state.dependants.reduce((total, dependant) => total + dependant.income.netIncome + dependant.income.netPensionIncome, 0),
    dependantGrossIncome: state => state.dependants.reduce((total, dependant) => total + extractIncome(dependant, 'gross'), 0),
    // durationOfDependency: (state, g, rs, rg) => determineDependency(state, rg),
    maxDuration: (state, g, rs, rg) => state.dependants.reduce((duration, dependant, index) => duration, determineDependency(state, rg)(0)),
    hasSplitPeriods: (state) => {
        if (!state.theDeceased.income.unchangedPension || !state.theDeceased.income.retired) {
            return true;
        }
        return state.dependants.reduce((hasSplit, dependant) => hasSplit || !dependant.income.unchangedPension || !dependant.income.retired, false);
    },
    getDeceasedIncomePeriod: state => index => state.theDeceased.income.periods[index],
    getDependantIncomePeriod: state => (dependant, index) => state.dependants[dependant].income.periods[index],
    getDependencyOnIncomeValid: state => state.dependencyOnIncomeValid,
    getNoOfDependantPeriods: state => (index) => state.dependants[index].income.periods.length
};

/** ********************************************************
 *                         Actions                        *
 **********************************************************/

// /////////////////////////////////////////////////////////
//                  Helper Functions                     //
// /////////////////////////////////////////////////////////

/**
 * Cycle over an object and dispatch an update for each key.
 *
 * @param {function} dispatch
 * @param {string} action
 * @param {{}} obj
 * @param {number|null} [index]
 *
 * @return {Promise}
 */
const updateObject = (dispatch, action, obj, index = null) => {
    const proms = Object.keys(obj).map(key => dispatch(action, {key, value: obj[key], index}));
    return Promise.all(proms);
};

/**
 * When a field on a person updated, dispatch the commanding actions.
 *
 * Action are defined as arguments to the first function, then the second function is the trigger
 *
 * @param {{[ageAtDeath]:string,incomeValue:string,retirementDate:string}} actions
 *
 * @return {function(*): Array<Promise>}} of updated to await
 */
const dynamicUpdates = actions => (key, dispatch, person, index) => {
    const proms = [];
    switch (key) {
        case 'dateOfDeath':
        case 'dateOfBirth':
            // Update age at death, if required
            if (actions.ageAtDeath) {
                proms.push(dispatch(actions.ageAtDeath, {
                    key: 'ageAtDeath',
                    value: calcAgeDeath(person),
                    index,
                    force: true,
                }));
            }
            // Update life expectancy now dob has changed
            proms.push(dispatch(actions.lifeExpectancy, {lifeExpectancy: person.lifeExpectancy, index}));
            if (key == 'dateOfDeath') { // We don't need to check retirment date for date of death
                break;
            }
        // Dob we do update retirement so fall through
        case 'gender':
            // If dob or gender changes, update default retirement age
            // ad you can can't alter DOB/Gender on the same page a retirement age, so should appear correct
            proms.push(
                dispatch(actions.incomeValue, {
                    key: 'retirementAge',
                    value: DEFAULT_RETIREMENT[person.gender],
                    index,
                }).then(() => dispatch(actions.retirementDate, {index}))
            );
    }
    return proms;
};

/**
 * Define the update actions in a functional way to digest by deceased
 *
 * Curried function configured for when deceased values change
 *
 * @type {function(key, dispatch, person, index):Array.<Promise>}
 */
const theDeceasedEvents = dynamicUpdates({
    ageAtDeath: 'updateTheDeceasedValue',
    lifeExpectancy: 'updateTheDeceasedLifeExpectancy',
    incomeValue: 'updateTheDeceasedIncomeValue',
    retirementDate: 'updateRetirementAgeAndDate',
});

/**
 * Define the update actions in a functional way to digest for dependant
 *
 * Curried function configured for when deceased values change
 *
 * @type {function(key, dispatch, person, index):Array.<Promise>}
 */
const dependantUpdateEvents = dynamicUpdates({
    lifeExpectancy: 'updateDependantLifeExpectancy',
    incomeValue: 'updateDependantIncomeValue',
    retirementDate: 'updateDependantRetirementDate',
});

/**
 * Filter to define variables that a user can set directly on a person (stop's nest properties being set)
 * @return {function(*): boolean}
 */
const userSettable = key => key !== 'lifeExpectancy' && key !== 'income' && key !== 'ageAtDeath';

/**
 * After the income on person, has been update, determine from updated keys other action to dispatch
 *
 * @param {{updateRetirementAge:string,updateRetirementDate:string, updateValue:string }} actions the action to person
 * @param {function} dispatch
 * @param {incomeTemplate} income
 * @param {string} dateOfDeath
 * @param {Number} [index]
 *
 * @return {function(*) : Array<Promise>}
 */
const incomeUpdates = (actions, dispatch, income, dateOfDeath, index) => (updatedKeys) => {
    const proms = [];
    // If retirement date updates
    if (~updatedKeys.indexOf('retirementDate')) {
        proms.push(dispatch(actions.updateRetirementAge, {index}));
    }
    // If retirement age updates
    if (~updatedKeys.indexOf('retirementAge')) {
        proms.push(dispatch(actions.updateRetirementDate, {index}));
    }

    return Promise.all(proms);
};

/**
 * Used to determine if the income values for pension have changed form the set of changed keys from income.
 *
 * @param income
 * @param updatedKeys
 * @return {boolean}
 */
const doesIncomeNeedToUpdate = (income, updatedKeys) => {
    const triggerKeys = [
        'retired',
        'grossIncome',
        'netIncome',
        'employmentType',
        'netPensionIncome',
        'grossPensionIncome',
    ];
    const keys = updatedKeys.filter(key => key && ~triggerKeys.indexOf(key));
    return !!keys.length;
};
const doesIncomePeriodNeedToUpdate = (updatedKeys) => {
    const triggerKeys = [
        'startOfPeriod',
        'endOfPeriod',
        'employmentType',
        'employmentIncome',
        'employmentNetGross',
        'pensionIncome',
        'pensionNetGross',
        'grossIncome',
        'netIncome',
        'netPensionIncome',
        'grossPensionIncome',
    ];
    const keys = updatedKeys.filter(key => key && ~triggerKeys.indexOf(key));
    return !!keys.length;
};

/**
 * Throttle update of person life expectancy to allow for typing
 */
export const throttleUpdateLife = _.debounce((dispatch, action, person, lifeExpectancy, index) => {
    if (validDate(person.dateOfBirth)) {
        let assumedSettlement = store.getters['fatal/dependency/claimDetails/value']('assumedSettlement');
        determineLife(assumedSettlement, person, lifeExpectancy)
            .then(updatedLife => updateObject(dispatch, action, updatedLife, index))
            .then(() => dispatch('updatePeriodsForLifeExpectancy'));
    }
}, 500);

/**
 * Is a Valid Income Period Provided
 * @param  {Object} period
 * @return {boolean}
 */
const isValidIncome = (period) =>
    validDate(period.startOfPeriod) &&
    validDate(period.endOfPeriod) &&
    !isNaN(period.employmentIncome) &&
    !isNaN(period.pensionIncome) &&
    period.employmentIncome > 0 ||
    period.pensionIncome > 0;

/**
 * If zeroes are provided then reset the totals to zero
 * @param  {Function} (dispatch)
 * @param  {int} index
 * @param  {Object} period
 * @param  {Object} person
 * @param  {string} action
 * @param  {string} type
 */
const zeroCheck = (dispatch, index, period, person, action, type) => {
    if (type === 'deceased') {
        if (person.income.retired === true && period.pensionIncome === 0) {
            dispatch(action, {key:'totalNetIncome' , value:0, index});
        }
        if (person.income.retired === false && period.pensionIncome === 0 && period.employmentIncome === 0) {
            dispatch(action, {key:'totalNetIncome' , value:0, index});
            dispatch(action, {key:'totalGrossIncome' , value:0, index});
        }
    } else {
        if (person.income.retired === true && period.pensionIncome === 0) {
            dispatch(action, {key:'totalNetIncome' , value:0, index, dependant:person});
        }
        if (person.income.retired === false && period.pensionIncome === 0 && period.employmentIncome === 0) {
            dispatch(action, {key:'totalNetIncome' , value:0, index, dependant:person});
            dispatch(action, {key:'totalGrossIncome' , value:0, index, dependant:person});
        }
    }
};

/**
 * Perform a Throttle Income API request
 *
 * @param  {Function} (period, dispatch, action, args, deceased, dependant)
 * @return {Promise}
 */
const throttledIncome = _.debounce((period, dispatch, action, args, deceased, dependant) => Api.calculators.monetary.income(period, deceased, dependant)
    .then((updated) => {
        Object.keys(updated)
            .forEach((key) => {
                if (updated[key] !== null) {
                    dispatch(action, {key, value: updated[key], ...args});
                }
            });
    })
    .then(() => dispatch('fatal/estate/updateTheMultiplicand', null, {root: true})), 500);

/**
 * Perform an Unthrottled Income API request
 * Used only for a updating an array of values. Never by the user
 *
 * @param  {Function} (period, dispatch, action, args, deceased, dependant)
 * @return {Promise}
 */
const unthrottledIncome = (period, dispatch, action, args, deceased, dependant) => Api.calculators.monetary.income(period, deceased, dependant)
    .then((updated) => {
        Object.keys(updated)
            .forEach((key) => {
                if (updated[key] !== null) {
                    dispatch(action, {key, value: updated[key], ...args});
                }
            });
    });


// /////////////////////////////////////////////////////////
//                     Public Actions                    //
// /////////////////////////////////////////////////////////
const actions = {
    /**
     * Update all values on the deceased
     *
     * @param dispatch
     * @param theDeceased
     * @return {Promise}
     */
    updateTheDeceased({dispatch}, theDeceased) {
        return updateObject(dispatch, 'updateTheDeceasedValue', theDeceased);
    },

    /**
     * Update a single deceased value
     *
     * Triggers defendant update on ../fatal store and
     *
     * @param {function} commit
     * @param {state} state
     * @param {function} dispatch
     * @param key
     * @param value
     * @param force
     */
    updateTheDeceasedValue({commit, state, dispatch}, {key, value, force = false}) {
        if (state.theDeceased[key] !== value && (force || userSettable(key))) {
            commit(mutationTypes.UPDATE_DECEASED, {key, value});
            // Update other values that rely on deceased values
            return Promise.all([
                ...theDeceasedEvents(key, dispatch, state.theDeceased),
            ]);
        }
    },

    updateTheDeceasedLifeExpectancy({dispatch, state}, {lifeExpectancy}) {
        return updateObject(dispatch, 'updateTheDeceasedLifeExpectancyValue', lifeExpectancy)
            .then(() => throttleUpdateLife(dispatch, 'updateTheDeceasedLifeExpectancyValue', state.theDeceased, lifeExpectancy));
    },
    updateTheDeceasedLifeExpectancyValue({commit, state}, {key, value}) {
        if (state.theDeceased.lifeExpectancy[key] !== value) {
            commit(mutationTypes.UPDATE_DECEASED_LIFE_EXPECTANCY, {key, value});
        }
    },
    updateTheDeceasedIncome({dispatch, state, getters}, {income}) {
        const actions = {
            updateRetirementAge: 'updateTheDeceasedRetirementAge',
            updateRetirementDate: 'updateTheDeceasedRetirementDate',
            updateValue: 'updateTheDeceasedIncomeValue',
        };
        const dependencyDuration = getters.durationOfDependency(0);
        // Resolve all changed values
        return updateObject(dispatch, 'updateTheDeceasedIncomeValue', income)
            .then(incomeUpdates(actions, dispatch, income, state.theDeceased.dateOfDeath, dependencyDuration));
    },
    updateTheDeceasedIncomeValue({commit, state}, {key, value}) {
        if (state.theDeceased.income[key] === value) {
            return;
        }
        commit(mutationTypes.UPDATE_DECEASED_INCOME, {key, value});
        return key;
    },
    updateTheDeceasedRetirementDate({commit, state}) {
        if (!validDate(state.theDeceased.dateOfBirth)) {
            return;
        }
        const date = moment(state.theDeceased.dateOfBirth, 'DD/MM/YYYY')
            .add(state.theDeceased.income.retirementAge, 'years')
            .format('DD/MM/YYYY');
        if (!date || state.theDeceased.income.retirementDate === date) {
            return;
        }
        commit(mutationTypes.UPDATE_DECEASED_INCOME, {key: 'retirementDate', value: date});
    },
    updateTheDeceasedRetirementAge({commit, state}) {
        if (!validDate(state.theDeceased.dateOfBirth) || !validDate(state.theDeceased.income.retirementDate)) {
            return;
        }
        const age = moment(state.theDeceased.income.retirementDate, 'DD/MM/YYYY')
            .diff(moment(state.theDeceased.dateOfBirth, 'DD/MM/YYYY'), 'years', true);
        if (isNaN(age) || state.theDeceased.income.retirementAge === age) {
            return;
        }
        commit(mutationTypes.UPDATE_DECEASED_INCOME, {key: 'retirementAge', value: age});
    },
    addDependant({commit}) {
        commit(mutationTypes.ADD_DEPENDANT);
    },
    updateDependant({dispatch}, {index, dependant}) {
        return updateObject(dispatch, 'updateDependantValue', dependant, index);
    },
    updateDependantValue({commit, state, dispatch}, {key, value, index}) {
        if (state.dependants[index][key] !== value && userSettable(key)) {
            commit(mutationTypes.UPDATE_DEPENDANT, {key, value, index});
            // Update other values that rely on dependant values
            return Promise.all(dependantUpdateEvents(key, dispatch, state.dependants[index], index));
        }
    },
    updateDependantLifeExpectancy({dispatch, state}, {index, lifeExpectancy}) {
        return updateObject(dispatch, 'updateDependantLifeExpectancyValue', lifeExpectancy, index)
            .then(() => throttleUpdateLife(
                dispatch,
                'updateDependantLifeExpectancyValue',
                state.dependants[index],
                lifeExpectancy,
                index),
            );
    },
    updateDependantLifeExpectancyValue({commit, state}, {key, value, index}) {
        if (state.dependants[index].lifeExpectancy[key] !== value) {
            commit(mutationTypes.UPDATE_DEPENDANT_LIFE_EXPECTANCY, {key, value, index});
        }
    },
    updateDependantYouth({commit}, {key, value, index}) {
        commit(mutationTypes.UPDATE_DEPENDANT_LIFE_EXPECTANCY, {key, value, index});
    },
    updateDependantIncome({dispatch, state}, {index, income}) {
        const actions = {
            updateRetirementAge: 'updateDependantRetirementAge',
            updateRetirementDate: 'updateDependantRetirementDate',
            updateValue: 'updateDependantIncomeValue',
        };
        // Resolve all changed values
        return updateObject(dispatch, 'updateDependantIncomeValue', income, index)
            .then(incomeUpdates(actions, dispatch, income, state.theDeceased.dateOfDeath, index));
    },
    updateDependantRetirementDate({commit, state}, {index}) {
        if (!validDate(state.dependants[index].dateOfBirth)) {
            return;
        }
        const date = moment(state.dependants[index].dateOfBirth, 'DD/MM/YYYY')
            .add(state.dependants[index].income.retirementAge, 'years')
            .format('DD/MM/YYYY');
        if (!date || state.dependants[index].income.retirementDate === date) {
            return;
        }
        commit(mutationTypes.UPDATE_DEPENDANT_INCOME, {key: 'retirementDate', value: date, index});
    },
    updateDependantRetirementAge({commit, state}, {index}) {
        if (!validDate(state.dependants[index].dateOfBirth) || !validDate(state.dependants[index].income.retirementDate)) {
            return;
        }
        const age = moment(state.dependants[index].income.retirementDate, 'DD/MM/YYYY')
            .diff(moment(state.dependants[index].dateOfBirth, 'DD/MM/YYYY'), 'years', true);
        if (isNaN(age) || state.dependants[index].income.retirementAge === age) {
            return;
        }
        commit(mutationTypes.UPDATE_DEPENDANT_INCOME, {key: 'retirementAge', value: age, index});
    },
    updateDependantIncomeValue({commit, state}, {key, value, index}) {
        if (state.dependants[index].income[key] === value) {
            return;
        }
        commit(mutationTypes.UPDATE_DEPENDANT_INCOME, {key, value, index});
        return key;
    },
    deleteDependant({commit, state, dispatch}, index) {
        commit(mutationTypes.DELETE_DEPENDANT, index);
        if (!state.dependants.length) {
            return dispatch('addDependant');
        }
    },
    calcDurationForDependant({getters}, index) {
        return getters.durationOfDependency(index);
    },
    // updateTheDeceasedFromServer({commit}, serverStep) {
    //     commit(mutationTypes.RESET_DECEASED);
    //     commit(mutationTypes.SERVER_UPDATE_DECEASED, serverStep.data);
    //     return true;
    // },
    // updateDependantsFromServer({commit}, serverStep) {
    //     commit(mutationTypes.RESET_DEPENDANTS);
    //     serverStep.data.dependants.map((dependant, index) => commit(mutationTypes.SERVER_UPDATE_DEPENDANT, {
    //         dependant,
    //         index
    //     }));
    // },
    // updateIncomesFromServer({commit, getters}, serverStep) {
    //     commit(mutationTypes.SERVER_UPDATE_DECEASED_INCOME, serverStep.data);
    //     serverStep.data.dependants.forEach(
    //         (income, index) => commit(mutationTypes.SERVER_UPDATE_DEPENDANT_INCOME, {index, income}),
    //     );
    //     return getters.allDependantsValid;
    // },

    addDeceasedIncomePeriod({commit, state}) {
        let periods = state.theDeceased.income.periods,
            start = findNextPeriodStart(periods, state.theDeceased.dateOfDeath),
            end = findNextPeriodEnd(periods, state.theDeceased.lifeExpectancy.dateOfDeath);

        commit(mutationTypes.ADD_DECEASED_INCOME_PERIOD, defaultIncomePeriodTemplate({
            start: start,
            end: end,
        }));
    },
    updateDependencyOnIncomeValid({commit}, valid) {
        commit(mutationTypes.UPDATE_DEPENDENCY_ON_INCOME_VALID, valid);
    },
    updateDeceasedIncomePeriod({dispatch}, {index, period, deceased}) {
        updateObject(dispatch, 'updateDeceasedIncomePeriodValue', period, index)
            .then(updatedKeys => {
                if (!doesIncomePeriodNeedToUpdate(updatedKeys)) {
                    return;
                }
                zeroCheck(dispatch, index, period, deceased, 'updateDeceasedIncomePeriodValue', 'deceased');
                if (!isValidIncome(period) || deceased.income.retirementAge < 50 || deceased.income.retirementAge === "") {
                    return;
                }
                return throttledIncome(period, dispatch, 'updateDeceasedIncomePeriodValue', {index}, deceased);
            });
    },
    updateDeceasedIncomePeriodValue({commit, state}, {index, key, value}) {
        if (state.theDeceased.income.periods[index]) {
            let currentPeriod = state.theDeceased.income.periods[index];
            if (currentPeriod[key] === value) {
                return;
            }
            commit(mutationTypes.UPDATE_DECEASED_INCOME_PERIOD, {index, key, value});
            return key;
        }
    },
    deleteDeceasedIncomePeriod({commit, state, dispatch}, {index}) {
        commit(mutationTypes.DELETE_DECEASED_INCOME_PERIOD, index);
        if (!state.theDeceased.income.periods.length) {
            return dispatch('addDeceasedIncomePeriod');
        }
    },
    addDependantIncomePeriod({commit, state}, {index}) {
        let person = state.dependants[index];
        let periods = person.income.periods;

        commit(mutationTypes.ADD_DEPENDANT_INCOME_PERIOD,
            {
                dependant: index,
                period: defaultIncomePeriodTemplate({
                    start: findNextPeriodStart(periods, state.theDeceased.dateOfDeath),
                    end: findNextPeriodEnd(periods, state.theDeceased.lifeExpectancy.dateOfDeath),
                })
            });
    },
    updateDependantIncomePeriod({dispatch, state}, {index, period, dependant}) {
        let proms = Object.keys(period)
            .map(key => dispatch('updateDependantIncomePeriodValue', {key, value: period[key], index, dependant}));
        Promise.all(proms)
            .then(updatedKeys => {
                if (!doesIncomePeriodNeedToUpdate(updatedKeys)) {
                    return
                }
                zeroCheck(dispatch, index, period, dependant, 'updateDependantIncomePeriodValue', 'dependant');
                if (!isValidIncome(period)) {
                    return;
                }
                throttledIncome(period, dispatch, 'updateDependantIncomePeriodValue', {
                    index,
                    dependant,
                }, state.theDeceased, state.dependants[dependant]);
            })
    },
    updateDependantIncomePeriodValue({commit}, {index, key, value, dependant}) {
        let currentPeriod = dependant.income.periods[index];
        if (currentPeriod[key] === value) {
            return;
        }
        commit(mutationTypes.UPDATE_DEPENDANT_INCOME_PERIOD, {index, key, value, dependant});
        return key;
    },
    deleteDependantIncomePeriod({commit, dispatch}, {dependant, index}) {
        commit(mutationTypes.DELETE_DEPENDANT_INCOME_PERIOD, {dependant, index});
        if (!dependant.income.periods.length) {
            return dispatch('addDependantIncomePeriod', {index: dependant.index});
        }
    },
    updatePeriodsForDateOfDeath({commit, state, dispatch}) {
        let dateOfDeath = state.theDeceased.dateOfDeath;
        if (state.theDeceased.income.periods.length) {
            commit(mutationTypes.UPDATE_DECEASED_INCOME_PERIOD, {index: 0, key: 'startOfPeriod', value: dateOfDeath});
        }
        state.dependants.forEach((dependant, index) => {
            if (dependant.income.periods.length) {
                commit(mutationTypes.UPDATE_DEPENDANT_INCOME_PERIOD, {
                    index: 0,
                    key: 'startOfPeriod',
                    value: dateOfDeath,
                    dependant: state.dependants[index]
                });
            }
        });
        dispatch('fatal/estate/updateTheMultiplicand', null, {root: true});
    },
    updatePeriodsForLifeExpectancy({commit, state, dispatch}) {
        let dateOfDeath = state.theDeceased.lifeExpectancy.dateOfDeath;
        if (state.theDeceased.income.periods.length) {
            commit(mutationTypes.UPDATE_DECEASED_INCOME_PERIOD,
                {index: state.theDeceased.income.periods.length - 1, key: 'endOfPeriod', value: dateOfDeath});
        }
        state.dependants.forEach((dependant, index) => {
            if (dependant.income.periods.length) {
                commit(mutationTypes.UPDATE_DEPENDANT_INCOME_PERIOD, {
                    index: dependant.income.periods.length - 1,
                    key: 'endOfPeriod',
                    value: state.theDeceased.lifeExpectancy.dateOfDeath,
                    dependant: state.dependants[index]
                });
            }
            else if (dependant.lifeExpectancy.dateOfDeath) {
                dispatch('addDependantIncomePeriod', {index});
            }
        });
        dispatch('fatal/estate/updateTheMultiplicand', null, {root: true});
    },
    removeDeceasedInvalidPeriodAndAddDefaults({commit, state, dispatch}) {
        let dateOfDeath = state.theDeceased.dateOfDeath;

        // Remove the invalid periods
        state.theDeceased.income.periods.forEach(function (period, index) {
            if (state.theDeceased.income.retired) {
                commit(mutationTypes.UPDATE_DECEASED_INCOME_PERIOD, {index, key: 'employmentIncome', value: 0});
                throttledIncome(period, dispatch, 'updateDeceasedIncomePeriodValue', {index}, state.theDeceased);
            }

            let periodStart = moment(period.startOfPeriod, 'DD/MM/YYYY'),
                periodEnd = moment(period.endOfPeriod, 'DD/MM/YYYY');

            if (periodStart.isBefore(dateOfDeath)) {
                dispatch('deleteDeceasedIncomePeriod', {index})
            }
        });
    },
    removeDependantInvalidPeriodAndAddDefaults({dispatch, state}, {index}) {
        let dateOfDeath = state.dependants[index].dateOfDeathAssumed;

        // Remove the invalid periods
        state.dependants[index].income.periods.forEach((period, index) => {
            if (state.dependants[index].income.retired) {
                dispatch('updateDependantIncomePeriodValue', {
                    key: 'employmentIncome',
                    value: 0,
                    index,
                    dependant: state.dependants[index]
                });
                throttledIncome(period, dispatch, 'updateDependantIncomePeriodValue', {
                    index,
                    dependant: state.dependants[index]
                }, state.theDeceased, state.dependants[index]);
            }

            let periodStart = moment(period.startOfPeriod, 'DD/MM/YYYY'),
                periodEnd = moment(period.endOfPeriod, 'DD/MM/YYYY');

            if (periodStart.isBefore(dateOfDeath)) {
                dispatch('deleteDependantIncomePeriod', {index, dependant: state.dependants[index]})
            }
        });
    },
    updateAllIncome({dispatch, state}, data) {
        if (data.type === 'Deceased') {
             let updateIncome =  state.theDeceased.income.periods.map((period, index) => {
                if (!isValidIncome(period) || data.income.retirementAge < 50 || data.income.retirementAge === "") {
                    return;
                }
                unthrottledIncome(period, dispatch, 'updateDeceasedIncomePeriodValue', {index}, state.theDeceased);
            });

            dispatch('fatal/estate/updateTheMultiplicand', null, {root: true});

            return updateIncome;
        }

        let dependant = data.index || null;
        data.income.periods.map((period, index) => {
            if (!isValidIncome(period)) {
                return;
            }
            unthrottledIncome(period, dispatch, 'updateDependantIncomePeriodValue', {
                index,
                dependant
            }, state.theDeceased, data.income)
        });

        dispatch('fatal/estate/updateTheMultiplicand', null, {root: true});
    },
    reset({commit}) {
        commit(mutationTypes.RESET_DECEASED);
        commit(mutationTypes.RESET_DEPENDANTS);
    },
    updateRetirementAgeAndDate: _.debounce(({ state, commit }) => {
        return Api.calculators.dates.statePensionAge(state.theDeceased)
            .then((data) => {
                commit(mutationTypes.UPDATE_DECEASED_INCOME, {key: 'retirementAge', value: data.statePension.retirementAge});
                commit(mutationTypes.UPDATE_DECEASED_INCOME, {key: 'retirementDate', value: data.statePension.retirementDate});
            });
    }, 500),
};


/** ********************************************************
 *                       Mutations                        *
 **********************************************************/

export const mutatePersonFromServer = (person, payload, type) => {
    const dob = moment(payload.date_of_birth, 'YYYY-MM-DD');
    const dod = moment(payload.date_of_death, 'YYYY-MM-DD');
    const onsetOfSymptoms = moment(payload.onset_of_symptoms, 'YYYY-MM-DD');

    person.name = payload.name;
    person.gender = payload.gender;
    person.onsetOfSymptoms = onsetOfSymptoms.format('DD/MM/YYYY');
    person.dateOfBirth = dob.format('DD/MM/YYYY');
    if (dod.isValid()) {
        person.dateOfDeath = dod.isValid() ? dod.format('DD/MM/YYYY') : null;
        person.ageAtDeath = calcAgeDeath(person);
    }
    person.lifeExpectancy = payload.full_life;
    person.income = payload.income;

    return true;
};

const mutatePersonIncomeFromServer = (income, payload, prefix = '') => {
    income.retired = payload[`${prefix}retired`];
    income.retirementAge = payload[`${prefix}retirement_age`];
    income.retirementDate = dateFromServer(payload[`${prefix}retirement_date`]);
    income.unchangedPension = payload[`${prefix}pension_unchanged`];
    income.netIncome = payload[`${prefix}net_income`];
    income.grossIncome = payload[`${prefix}gross_income`];
    income.netPensionIncome = payload[`${prefix}net_pension_income`];
    income.grossPensionIncome = payload[`${prefix}gross_pension__income`];
    income.employmentType = payload[`${prefix}employment_type`];
    income.periods = payload[`${prefix}periods`];
};

const mutations = {
    [mutationTypes.UPDATE_DECEASED](state, {key, value}) {
        state.theDeceased[key] = value;
    },
    [mutationTypes.RESET_DECEASED](state) {
        state.theDeceased = personTemplate();
    },
    [mutationTypes.UPDATE_DECEASED_LIFE_EXPECTANCY](state, {key, value}) {
        state.theDeceased.lifeExpectancy[key] = value;
    },
    [mutationTypes.UPDATE_DECEASED_INCOME](state, {key, value}) {
        state.theDeceased.income[key] = value;
    },
    [mutationTypes.ADD_DEPENDANT](state) {
        const dependant = personTemplate();
        dependant.index = state.dependants.length;
        state.dependants.push(dependant);
    },
    [mutationTypes.UPDATE_DEPENDANT](state, {key, value, index}) {
        state.dependants[index][key] = value;
    },
    [mutationTypes.RESET_DEPENDANTS](state) {
        state.dependants = [];
    },
    [mutationTypes.UPDATE_DEPENDANT_LIFE_EXPECTANCY](state, {key, value, index}) {
        state.dependants[index].lifeExpectancy[key] = value;
    },
    [mutationTypes.UPDATE_DEPENDANT_INCOME](state, {key, value, index}) {
        state.dependants[index].income[key] = value;
    },
    [mutationTypes.DELETE_DEPENDANT](state, index) {
        state.dependants.splice(index, 1);
    },
    [mutationTypes.SERVER_UPDATE_DECEASED](state, payload) {
        state.theDeceased = personTemplate();
        return mutatePersonFromServer(state.theDeceased, payload, 'deceased');
    },
    [mutationTypes.SERVER_UPDATE_DECEASED_INCOME](state, payload) {
        return mutatePersonIncomeFromServer(state.theDeceased.income, payload, 'deceased_');
    },
    [mutationTypes.SERVER_UPDATE_DEPENDANT_INCOME](state, {index, income}) {
        return mutatePersonIncomeFromServer(state.dependants[index].income, income);
    },
    [mutationTypes.SERVER_UPDATE_DEPENDANT](state, {dependant}) {
        const index = state.dependants.length;
        state.dependants.push(personTemplate());
        state.dependants[index].index = index;
        return mutatePersonFromServer(state.dependants[index], dependant, 'dependant');
    },
    [mutationTypes.ADD_DECEASED_INCOME_PERIOD](state, payload) {
        state.theDeceased.income.periods.push(payload);
        state.theDeceased.income.netPensionIncome += payload.netPensionIncome;
        state.theDeceased.income.grossPensionIncome += payload.grossPensionIncome;
        state.theDeceased.income.netIncome += payload.netIncome;
        state.theDeceased.income.grossIncome += payload.grossIncome;
    },
    [mutationTypes.UPDATE_DECEASED_INCOME_PERIOD](state, {key, value, index}) {
        if (~key.indexOf('Income')) {
            state.theDeceased.income[key] -= (state.theDeceased.income.periods[index][key] || 0);
        }
        state.theDeceased.income.periods[index][key] = value;
        if (~key.indexOf('Income')) {
            state.theDeceased.income[key] += (value || 0);
        }
    },
    [mutationTypes.DELETE_DECEASED_INCOME_PERIOD](state, payload) {
        state.theDeceased.income.periods.splice(payload, 1);
    },
    [mutationTypes.ADD_DEPENDANT_INCOME_PERIOD](state, {dependant, period}) {
        state.dependants[dependant].income.periods.push(period);
        state.dependants[dependant].income.netPensionIncome += period.netPensionIncome;
        state.dependants[dependant].income.grossPensionIncome += period.grossPensionIncome;
        state.dependants[dependant].income.netIncome += period.netIncome;
        state.dependants[dependant].income.grossIncome += period.grossIncome;
    },
    [mutationTypes.UPDATE_DEPENDANT_INCOME_PERIOD](state, {dependant, key, index, value}) {
        const income = state.dependants[dependant.index].income;
        if (~key.indexOf('Income')) {
            income[key] -= (income.periods[index][key] || 0);
        }
        income.periods[index][key] = value;
        if (~key.indexOf('Income')) {
            income[key] += (value || 0);
        }
    },
    [mutationTypes.DELETE_DEPENDANT_INCOME_PERIOD](state, {dependant, index}) {
        state.dependants[dependant.index].income.periods.splice(index, 1);
    },
    [mutationTypes.UPDATE_DEPENDENCY_ON_INCOME_VALID](state, valid) {
        state.dependencyOnIncomeValid = valid;
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/fatal/estate/people.js